/**
 * @file index.tsx
 *
 * @fileoverview Renders a range date picker.
 */
import { dateLocalesMap } from "./../../../01-forms/06-date/utils";
import { FormItem } from "./../../../01-forms/11-form-item";
import { FormLabel } from "./../../../01-forms/11-form-item/01-form-label";
import { FormDescription } from "./../../../01-forms/11-form-item/02-form-description";
import { useIsMobile } from "./../../../../../hooks/useIsMobile";
import format from "date-fns/format";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import styles from "../date.module.scss";
import "react-datepicker/dist/react-datepicker.css";
import { cva } from "class-variance-authority";
import useMaxDate from "../hooks/useMaxDate";

const wrapper = cva([styles.wrapper], {
  variants: {
    error: {
      true: styles.wrapperError,
    },
  },
});

export interface RangeDatePopupProps {
  /**
   * Marks if a field is in an error state.
   *
   * @default false
   */
  error?: boolean;
  /**
   * If the field is required.
   *
   * @default false
   */
  required?: boolean;
  /**
   * Id for the start date input.
   */
  startDateId?: string;
  /**
   * Id for the end date input.
   */
  endDateId?: string;
  /**
   * Date picker label.
   */
  label?: string;
  /**
   * Description for this field.
   */
  description?: string;
  /**
   * Start date aria label (if no label set).
   */
  startDateAriaLabel?: string;
  /**
   * End date aria label (if no label set).
   */
  endDateAriaLabel?: string;
  /**
   * Start date placeholder text.
   */
  startDatePlaceholderText?: string;
  /**
   * End date placeholder text.
   */
  endDatePlaceholderText?: string;
  /**
   * Initial value for start date.
   */
  initialStartDate?: any;
  /**
   * Initial value for end date.
   */
  initialEndDate?: any;
  /**
   * Even to trigger when dates change.
   */
  onDatesChange: (date: any) => void;
  /**
   * Format to display the dates in.
   */
  displayFormat?: string;
  /**
   * Min date value.
   */
  minDate?: any;
  /**
   * Max date value.
   */
  maxDate?: any;
  /**
   * If time selection should be visible.
   */
  showTimeSelect?: boolean;
  /**
   * The time format.
   */
  timeFormat?: string;
  /**
   * The intervals for each value in time select.
   */
  timeIntervals?: number;
  /**
   * The title for time selection.
   */
  timeCaption?: string;
  /**
   * If should be read only (prevent keyboard open).
   *
   * @default "false"
   */
  readOnly?: boolean;
  /**
   * Disabled keyboard navigation.
   */
  disabledKeyboardNavigation?: boolean;
  /**
   * To show a month and year dropdown.
   *
   * @default false
   */
  showMonthYearPicker?: boolean;
  /**
   * The format for the calendar
   */
  dateFormatCalendar?: string;
  /**
   * The supported calendar locale.
   */
  locale?: string;
}

/**
 * Accessible date popup can be used inside forms or filters in order to make it easier to choose a date. The range date picker is used
 * for multiple value dates like a start and end of education item or work experience. Use the "min and max dates" prop
 * to allow any date value to be inserted (including past dates). On mobile the default native date picker experience is used.
 */
export const RangeDatePopup = ({
  startDateId = "start_date",
  endDateId = "end_date",
  initialStartDate,
  initialEndDate,
  label,
  description,
  startDateAriaLabel,
  endDateAriaLabel,
  startDatePlaceholderText = "Start date",
  endDatePlaceholderText = "End date",
  onDatesChange,
  displayFormat = "dd/MM/yyyy",
  minDate,
  maxDate: maxDateProp,
  showTimeSelect = false,
  timeFormat = "HH:mm",
  timeIntervals = 15,
  timeCaption = "Time",
  readOnly = false,
  required = false,
  error = false,
  showMonthYearPicker = false,
  disabledKeyboardNavigation,
  locale,
  ...props
}: RangeDatePopupProps) => {
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [months, setMonths] = useState(1);
  const isMobile = useIsMobile();
  const maxDate = useMaxDate(maxDateProp);

  // Update number of months shown when window is small.
  useEffect(() => {
    if (isMobile) {
      setMonths(1);
    }
  }, [isMobile]);

  const datePicker = (
    <div className={wrapper({ error })}>
      {isMobile ? (
        <div className={styles.mobileRangeWrapper}>
          <input
            autoComplete="off"
            value={
              startDate
                ? showMonthYearPicker
                  ? format(startDate, "yyyy-MM")
                  : format(
                      startDate,
                      showTimeSelect ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd"
                    )
                : startDate
            }
            type={
              showMonthYearPicker
                ? "month"
                : showTimeSelect
                ? "datetime-local"
                : "date"
            }
            readOnly={readOnly}
            {...(minDate ? { min: minDate } : null)}
            max={maxDate.toISOString()}
            onChange={(event) => {
              const dateChanged = !!event.target.value
                ? new Date(event.target.value)
                : null;
              setStartDate(dateChanged);
              onDatesChange({
                startDate: dateChanged,
                endDate,
              });
            }}
          />
          <input
            autoComplete="off"
            value={
              endDate
                ? showMonthYearPicker
                  ? format(endDate, "yyyy-MM")
                  : format(
                      endDate,
                      showTimeSelect ? "yyyy-MM-dd'T'HH:mm" : "yyyy-MM-dd"
                    )
                : endDate
            }
            type={
              showMonthYearPicker
                ? "month"
                : showTimeSelect
                ? "datetime-local"
                : "date"
            }
            readOnly={readOnly}
            {...(startDate
              ? {
                  min: `${startDate.getFullYear()}-${String(
                    "00" + (startDate.getMonth() + 1)
                  ).slice(-2)}-${String("00" + startDate.getDate()).slice(-2)}`,
                }
              : null)}
            max={maxDate.toISOString()}
            onChange={(event) => {
              const dateChanged = !!event.target.value
                ? new Date(event.target.value)
                : null;
              setEndDate(dateChanged);
              onDatesChange({
                startDate,
                endDate: dateChanged,
              });
            }}
          />
        </div>
      ) : (
        <div className={styles.rangePickerWrapper}>
          <DatePicker
            {...(locale ? { locale: dateLocalesMap[locale] } : null)}
            autoComplete="off"
            selected={startDate}
            onChange={(date) => {
              setStartDate(date);
              onDatesChange({
                startDate: date,
                endDate,
              });
            }}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            id={startDateId}
            placeholderText={startDatePlaceholderText}
            dateFormat={displayFormat}
            monthsShown={months}
            readOnly={readOnly}
            showMonthYearPicker={showMonthYearPicker}
            {...(minDate ? { minDate } : null)}
            {...(maxDate ? { maxDate } : null)}
            {...(showTimeSelect
              ? {
                  showTimeSelect,
                  timeFormat,
                  timeCaption,
                  timeIntervals,
                }
              : null)}
            disabledKeyboardNavigation={disabledKeyboardNavigation}
            {...props}
          />
          <DatePicker
            {...(locale ? { locale: dateLocalesMap[locale] } : null)}
            autoComplete="off"
            selected={endDate}
            onChange={(date) => {
              setEndDate(date);
              onDatesChange({
                startDate,
                endDate: date,
              });
            }}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            id={endDateId}
            placeholderText={endDatePlaceholderText}
            monthsShown={months}
            dateFormat={displayFormat}
            minDate={startDate}
            readOnly={readOnly}
            showMonthYearPicker={showMonthYearPicker}
            {...(maxDate ? { maxDate } : null)}
            {...(showTimeSelect
              ? {
                  showTimeSelect,
                  timeFormat,
                  timeCaption,
                  timeIntervals,
                }
              : null)}
            disabledKeyboardNavigation={disabledKeyboardNavigation}
            {...props}
          />
        </div>
      )}
    </div>
  );
  if (!label) {
    return datePicker;
  }
  return (
    <FormItem>
      <FormLabel
        error={error}
        required={required}
        htmlFor={`${startDateId}-form-item`}
      >
        {label}
      </FormLabel>
      {datePicker}
      {description && <FormDescription>{description}</FormDescription>}
    </FormItem>
  );
};
export default RangeDatePopup;
